import { useBarcodeScanner } from '_hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';

interface SearchBarProps {
  placeholder?: string;
  onSearch: (searchTerm: string) => void;
  debounceMs?: number;
  className?: string;
  initialValue?: string;
  filterByDate?: string; // Optional date field name to filter by (e.g., 'sessionDate')
  onDateFilterChange?: (filterDate: string | null) => void; // Callback when date filter changes
  defaultDateFilter?: 'today' | 'all'; // Default date filter selection (defaults to 'today')
}

const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Search...',
  onSearch,
  debounceMs = 300,
  className = '',
  initialValue = '',
  filterByDate,
  onDateFilterChange,
  defaultDateFilter = 'today',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialValue);
  const [dateFilter, setDateFilter] = useState<'today' | 'all'>(
    defaultDateFilter,
  );

  const handleScan = useCallback((code: string) => {
    console.log('Scanned code:', code);
    console.log('Scanned code length:', code.length);
    console.log('Scanned code trimmed:', code.trim());
    console.log('Scanned code trimmed length:', code.trim().length);
    setSearchTerm(code);
  }, []);

  // Register the scanner listener
  useBarcodeScanner({
    onScan: handleScan,
    minLength: 6,
    scanTimeout: 60,
    terminator: 'Enter',
  });

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  // Trigger search when debounced value changes
  useEffect(() => {
    onSearch(debouncedSearchTerm);
  }, [debouncedSearchTerm, onSearch]);

  // Handle date filter changes
  useEffect(() => {
    if (filterByDate && onDateFilterChange) {
      if (dateFilter === 'today') {
        // Get today's date in YYYY-MM-DD format (local timezone)
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const todayLocal = `${yyyy}-${mm}-${dd}`;
        onDateFilterChange(todayLocal);
      } else {
        // All season - no date filter
        onDateFilterChange(null);
      }
    }
  }, [dateFilter, filterByDate, onDateFilterChange]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      // Immediately search on form submit
      onSearch(searchTerm);
    },
    [searchTerm, onSearch],
  );

  return (
    <div
      className={`bg-white p-4 rounded-lg shadow-sm border border-gray-200 ${className}`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MdSearch
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder={placeholder}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Search
          </button>
        </div>

        {/* Date Filter Radio Buttons */}
        {filterByDate && (
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 sm:p-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <span className="text-base sm:text-sm font-bold text-gray-800">
                Filter by Date:
              </span>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
                <label className="inline-flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="dateFilter"
                    value="today"
                    checked={dateFilter === 'today'}
                    onChange={e => setDateFilter('today')}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                  />
                  <span className="ml-3 text-base sm:text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                    Today
                  </span>
                </label>
                <label className="inline-flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    name="dateFilter"
                    value="all"
                    checked={dateFilter === 'all'}
                    onChange={e => setDateFilter('all')}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 cursor-pointer"
                  />
                  <span className="ml-3 text-base sm:text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                    All Season
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;
